import { User } from "../interfaces/user";
import { FirebaseAuthService } from "./firebase-auth";
import { UsersService } from "./users";
import { OrganizationsService } from "./organizations";
import { App } from "./app-state";
import { Access } from "./permissions";
import { Log } from "./log";

export class EmailNotVerifiedError extends Error {
  constructor() {
    super('email-not-verified');
    this.name = 'EmailNotVerifiedError';
  }
}

class AuthController {

  /**
   * Register the Firebase ID token listener.
   * Call once at app startup, after FirebaseAppService.initialize().
   * Keeps AppState in sync with Firebase Auth token auto-refresh.
   */
  initializeFirebaseTokenListener(): void {
    Log.debug('[auth] registering Firebase token listener');
    FirebaseAuthService.onTokenChanged(async (token, firebaseUser) => {
      Log.debug('[auth] onTokenChanged fired', { hasToken: !!token, uid: firebaseUser?.uid, emailVerified: firebaseUser?.emailVerified });
      if (token && firebaseUser && firebaseUser.emailVerified) { //
        Log.debug('[auth] valid token and verified user found — setting App state');
        await App.setState('jwt', token);
        const profile = await UsersService.getProfile(firebaseUser.uid);
        const user: User = profile ?? {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName,
          emailVerified: firebaseUser.emailVerified,
          organizations: [],
          defaultOrganizationId: null,
          createdAt: new Date(),
        };
        await App.setState('user', user);
        await this.loadAndSetOrganization(user);
        Log.debug('[auth] Firebase token refreshed and stored', { uid: firebaseUser.uid });
      } else {
        Log.debug('[auth] no valid token or unverified user — clearing session state');
        await this.clearSessionState();
      }
    });
  }

  async loginWithGoogle(): Promise<void> {
    // If running local development, use popup sign-in to avoid issues with localhost origins in redirects.
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    Log.debug('[auth] loginWithGoogle called', { hostname: window.location.hostname, isLocalhost });
    if (isLocalhost) {
      Log.debug('[auth] using popup sign-in for localhost');
      const credential = await FirebaseAuthService.signInWithGooglePopup();
      Log.debug('[auth] popup credential returned', { uid: credential?.user?.uid });
      const profile = await this.ensureGoogleProfile(credential.user);
      await this.storeFirebaseSessionInfo(profile);
      Log.debug('Google login complete (popup)');
    } else {
      Log.debug('[auth] initiating redirect sign-in (non-localhost)');
      await FirebaseAuthService.signInWithGoogleRedirect();
      // Note: signInWithGoogleRedirect navigates away — result will be processed on app load by handleGoogleRedirectResult
    }
  }

  async handleGoogleRedirectResult(): Promise<boolean> {
    Log.debug('[auth] handleGoogleRedirectResult() - attempting to read redirect result');
    const result = await FirebaseAuthService.getGoogleRedirectResult();
    Log.debug('[auth] getGoogleRedirectResult returned', { hasResult: !!result, uid: result?.user?.uid });
    if (result && result.user) {
      const profile = await this.ensureGoogleProfile(result.user);
      await this.storeFirebaseSessionInfo(profile);
      Log.debug('Google login complete (redirect)', { uid: result.user.uid });
      return true;
    } else {
      Log.debug('[auth] no redirect credential available — nothing to process');
      return false;
    }
  }

  private async ensureGoogleProfile(firebaseUser: { uid: string; email: string; displayName: string; }): Promise<User> {
    let profile = await UsersService.getProfile(firebaseUser.uid);
    if (!profile) {
      profile = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName,
        emailVerified: true,
        organizations: [],
        defaultOrganizationId: null,
        createdAt: new Date(),
      };
      await UsersService.createProfile(profile);
    }
    return profile;
  }

  async login(email: string, password: string): Promise<User> {
    const firebaseUser = await FirebaseAuthService.signInWithEmail(email, password);

    if (!firebaseUser.emailVerified) {
      await FirebaseAuthService.signOut();
      await App.setState('pendingVerificationEmail', firebaseUser.email);
      throw new EmailNotVerifiedError();
    }

    let profile = await UsersService.getProfile(firebaseUser.uid);
    if (!profile) {
      profile = {
        id: firebaseUser.uid,
        email: firebaseUser.email,
        name: firebaseUser.displayName,
        emailVerified: true,
        organizations: [],
        defaultOrganizationId: null,
        createdAt: new Date(),
      };
      await UsersService.createProfile(profile);
    }

    await this.storeFirebaseSessionInfo(profile);
    Log.debug('Login complete');
    return profile;
  }

  async register(email: string, password: string, displayName: string): Promise<void> {
    const firebaseUser = await FirebaseAuthService.createUser(email, password);

    await UsersService.createProfile({
      id: firebaseUser.uid,
      email: firebaseUser.email,
      name: displayName,
      emailVerified: false,
      organizations: [],
      defaultOrganizationId: null,
      createdAt: new Date(),
    });

    await FirebaseAuthService.sendVerificationEmail();
    await App.setState('pendingVerificationEmail', firebaseUser.email);
    // Sign out until email is verified — prevents the token listener from
    // treating the new unverified account as an active session.
    await FirebaseAuthService.signOut();
    Log.debug('Registration complete, verification email sent');
  }

  async resendVerificationEmail(): Promise<void> {
    await FirebaseAuthService.sendVerificationEmail();
    Log.debug('Verification email resent');
  }

  async initiatePasswordReset(email: string): Promise<void> {
    await FirebaseAuthService.sendPasswordResetEmail(email);
    Log.debug('Password reset initiated for', email);
  }

  async logout(): Promise<void> {
    try {
      await FirebaseAuthService.signOut();
    } catch (error) {
      Log.error('Firebase sign out failed', error);
    }
    await this.clearSessionState();
  }

  private async storeFirebaseSessionInfo(profile: User): Promise<void> {
    Log.debug('[auth] storing firebase session info for profile', { id: profile?.id, email: profile?.email });
    const token = await FirebaseAuthService.getIdToken();
    Log.debug('[auth] obtained id token', { hasToken: !!token, tokenPreview: token ? token.slice(0, 12) + '...' : null });
    if (token) {
      await App.setState('jwt', token);
    }
    await App.setState('user', profile);
    await this.loadAndSetOrganization(profile);
    Log.debug('Firebase session info stored and organization load attempted', { id: profile?.id });
  }

  private async loadAndSetOrganization(user: User): Promise<void> {
    const orgId = user?.defaultOrganizationId || user?.organizations?.[0]?.id;
    Log.debug('[auth] loadAndSetOrganization', { defaultOrganizationId: user?.defaultOrganizationId, firstOrgId: user?.organizations?.[0]?.id, resolvedOrgId: orgId });
    if (!orgId) {
      Log.debug('[auth] No organization to load — user has no defaultOrganizationId and no organizations');
      return;
    }
    try {
      const org = await OrganizationsService.getOrganization(orgId);
      Log.debug('[auth] Organization loaded', { orgId: org?.id, orgName: org?.name });
      Access.setOrganization(org);
      await App.setState('organization', org ? OrganizationsService.toRef(org) : null);
    } catch (error) {
      Log.error('Failed to load default organization', error);
    }
  }

  private async clearSessionState(): Promise<void> {
    Log.debug('[auth] clearing session state');
    Access.setOrganization(null);
    await App.setState('account', null);
    await App.setState('user', null);
    await App.setState('organization', null);
    await App.setState('refreshToken', null);
    await App.setState('jwt', null);
  }
}

export const AuthService = new AuthController();
